import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { DigestResult, Project } from '../types'
import { fmtDate } from '../lib/format'

export default function DigestTab({ project }: { project: Project }) {
  const [result, setResult] = useState<DigestResult | null>(null)
  const [running, setRunning] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [edit, setEdit] = useState('')
  const [editing, setEditing] = useState(false)
  const [gmailReady, setGmailReady] = useState(false)
  const [sendTo, setSendTo] = useState('')
  const [sending, setSending] = useState(false)
  const [sentMsg, setSentMsg] = useState<string | null>(null)

  useEffect(() => {
    void api.integrations.status().then((s) => setGmailReady(s.gmail.connected))
  }, [])

  async function generate() {
    setRunning(true)
    setErr(null)
    try {
      const data = await api.weeklyDigest(project.id)
      setResult(data)
      setEdit(data.digest)
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setRunning(false)
    }
  }

  function copyToClipboard() {
    void navigator.clipboard.writeText(editing ? edit : result?.digest ?? '')
  }

  async function sendViaGmail() {
    const body = editing ? edit : result?.digest
    if (!body || !sendTo) return
    setSending(true)
    setSentMsg(null)
    setErr(null)
    try {
      await api.integrations.gmailSend({
        to: sendTo,
        subject: `Heritage update — ${project.name}`,
        body,
      })
      setSentMsg(`Sent to ${sendTo}`)
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setSending(false)
    }
  }

  return (
    <section>
      <div className="row-between mb-3">
        <div>
          <h2>Weekly Digest</h2>
          <p className="muted small">
            AI drafts a 2-3 paragraph client update from the last 7 days of activity (walks, tasks,
            decisions, COs, inspections, draws). You read, edit if needed, send.
          </p>
        </div>
        <button className="btn-primary" onClick={generate} disabled={running}>
          {running ? 'Drafting…' : result ? 'Re-draft' : 'Draft Friday digest'}
        </button>
      </div>

      <div className="callout small mb-3">
        Voice trained on Dustin: direct, plain English, no marketing speak. Cost: ~$0.01 per draft.
        Doesn't auto-send — you copy + paste into your client email.
      </div>

      {err && <div className="card alert mb-3">{err}</div>}

      {result && (
        <>
          <div className="card mb-3">
            <div className="row-between mb-2">
              <h3>Generated draft</h3>
              <div className="row gap-2">
                <button className="btn-ghost small" onClick={() => setEditing(!editing)}>
                  {editing ? 'Done editing' : 'Edit'}
                </button>
                <button className="btn-primary small" onClick={copyToClipboard}>
                  Copy
                </button>
              </div>
            </div>
            {gmailReady && (
              <div className="row gap-2 mb-2">
                <input
                  type="email"
                  placeholder={project.client_email ?? 'client@example.com'}
                  value={sendTo}
                  onChange={(e) => setSendTo(e.target.value)}
                  style={{ flex: 1 }}
                />
                <button
                  className="btn-primary small"
                  onClick={sendViaGmail}
                  disabled={!sendTo || sending}
                >
                  {sending ? 'Sending…' : 'Send via Gmail'}
                </button>
              </div>
            )}
            {sentMsg && <div className="callout small mb-2" style={{ borderLeftColor: 'var(--ok)' }}>{sentMsg}</div>}
            {editing ? (
              <textarea
                rows={10}
                value={edit}
                onChange={(e) => setEdit(e.target.value)}
                style={{ width: '100%' }}
              />
            ) : (
              <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{result.digest}</pre>
            )}
          </div>

          <div className="card">
            <h3>Activity in this digest</h3>
            <ul className="kv">
              <li><span>Period</span><code>{result.period ? `${fmtDate(result.period.since)} → ${fmtDate(result.period.until)}` : 'last 7 days'}</code></li>
              <li><span>Site walks</span><code>{result.activity.walks}</code></li>
              <li><span>Tasks completed</span><code>{result.activity.tasks}</code></li>
              <li><span>Decisions resolved</span><code>{result.activity.decisions}</code></li>
              <li><span>Change orders</span><code>{result.activity.change_orders}</code></li>
              <li><span>Inspections</span><code>{result.activity.inspections}</code></li>
              <li><span>Draws</span><code>{result.activity.draws}</code></li>
            </ul>
          </div>
        </>
      )}
    </section>
  )
}
